import React, { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { TimesheetDayCell } from './TimesheetDayCell'
import type {
  TimesheetEmployee,
  TimesheetDay,
  TimesheetReportMeta,
} from '../../types/reports.types'

const WEEKDAY_NAMES = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']

interface SelectedDay {
  employee: TimesheetEmployee
  dateStr: string
  day: TimesheetDay
}

interface TimesheetGridProps {
  employees: TimesheetEmployee[]
  meta: TimesheetReportMeta
  onDayClick: (selection: SelectedDay) => void
}

interface OrganizationGroup {
  organizationId: string | null
  organizationName: string
  employees: TimesheetEmployee[]
}

const isWeekend = (date: Date): boolean => {
  const day = date.getDay()
  return day === 0 || day === 6
}

const isToday = (date: Date): boolean => {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

const formatDelta = (delta: number): string => {
  if (delta === 0) return '0'
  return delta > 0 ? `+${delta}` : `${delta}`
}

export function TimesheetGrid({
  employees,
  meta,
  onDayClick,
}: TimesheetGridProps) {
  // Group employees by organization
  const organizationGroups = useMemo((): OrganizationGroup[] => {
    const groups = new Map<string | null, TimesheetEmployee[]>()

    for (const emp of employees) {
      const key = emp.organization_id
      if (!groups.has(key)) {
        groups.set(key, [])
      }
      groups.get(key)!.push(emp)
    }

    return Array.from(groups.entries()).map(([orgId, emps]) => ({
      organizationId: orgId,
      organizationName: emps[0]?.organization_name || 'Без организации',
      employees: emps,
    }))
  }, [employees])

  // Track collapsed organizations
  const [collapsedOrgs, setCollapsedOrgs] = useState<Set<string | null>>(new Set())

  const toggleOrg = (orgId: string | null) => {
    setCollapsedOrgs((prev) => {
      const next = new Set(prev)
      if (next.has(orgId)) {
        next.delete(orgId)
      } else {
        next.add(orgId)
      }
      return next
    })
  }

  // Generate dates for the month
  const dates = useMemo(() => {
    const result: Date[] = []
    for (let day = 1; day <= meta.days_in_month; day++) {
      result.push(new Date(meta.year, meta.month - 1, day))
    }
    return result
  }, [meta.year, meta.month, meta.days_in_month])

  // Generate date strings for lookup
  const dateStrings = useMemo(() => {
    return dates.map((d) => {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    })
  }, [dates])

  const hasMultipleOrgs = organizationGroups.length > 1

  return (
    <div className="border rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
      <div className="overflow-auto flex-1">
        <table className="w-full border-collapse text-sm">
          {/* Header with dates */}
          <thead className="sticky top-0 z-30">
            <tr className="bg-muted">
              {/* Employee column header */}
              <th className="sticky left-0 z-40 bg-muted border-r min-w-[200px] p-2 text-left font-medium text-xs">
                Сотрудник
              </th>
              {/* Date headers */}
              {dates.map((date, idx) => {
                const dayOfWeek = date.getDay()
                const dayNum = date.getDate()
                const isTodayDate = isToday(date)
                const isWeekendDay = isWeekend(date)

                return (
                  <th
                    key={dateStrings[idx]}
                    className={cn(
                      'min-w-[32px] max-w-[32px] p-0.5 text-center text-[10px] font-medium border-l',
                      isTodayDate && 'bg-blue-100 border-blue-300',
                      isWeekendDay && !isTodayDate && 'bg-gray-100',
                    )}
                  >
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          'text-[9px]',
                          isWeekendDay ? 'text-red-500' : 'text-muted-foreground',
                        )}
                      >
                        {WEEKDAY_NAMES[dayOfWeek]}
                      </span>
                      <span
                        className={cn(
                          'font-bold',
                          isTodayDate && 'text-blue-600',
                          isWeekendDay && !isTodayDate && 'text-red-500',
                        )}
                      >
                        {dayNum}
                      </span>
                    </div>
                  </th>
                )
              })}
              {/* Summary columns */}
              <th className="sticky right-[80px] z-30 min-w-[50px] p-1 text-center text-[10px] font-medium border-l bg-green-50">
                Часов
              </th>
              <th className="sticky right-[40px] z-30 min-w-[40px] p-1 text-center text-[10px] font-medium border-l bg-blue-50">
                Норма
              </th>
              <th className="sticky right-0 z-30 min-w-[40px] p-1 text-center text-[10px] font-medium border-l bg-gray-100">
                +/-
              </th>
            </tr>
          </thead>
          {/* Body with employees */}
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td
                  colSpan={dates.length + 4}
                  className="p-8 text-center text-muted-foreground"
                >
                  Нет данных для отображения
                </td>
              </tr>
            ) : (
              organizationGroups.map((group) => {
                const isCollapsed = collapsedOrgs.has(group.organizationId)

                return (
                  <React.Fragment key={group.organizationId || 'no-org'}>
                    {/* Organization header row (only if multiple organizations) */}
                    {hasMultipleOrgs && (
                      <tr className="bg-slate-100">
                        <td
                          colSpan={dates.length + 4}
                          className="sticky left-0 z-10 p-2 font-medium text-xs cursor-pointer hover:bg-slate-200"
                          onClick={() => toggleOrg(group.organizationId)}
                        >
                          <div className="flex items-center gap-2">
                            {isCollapsed ? (
                              <ChevronRight className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                            <span>{group.organizationName}</span>
                            <span className="text-muted-foreground">
                              ({group.employees.length} сотр.)
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Employee rows */}
                    {!isCollapsed &&
                      group.employees.map((employee) => (
                        <tr key={employee.employee_id} className="hover:bg-muted/50">
                          {/* Employee name */}
                          <td className="sticky left-0 z-10 bg-background border-r p-1 min-w-[200px]">
                            <div className="flex flex-col">
                              <span className="font-medium text-xs truncate max-w-[180px]" title={employee.employee_name}>
                                {employee.employee_name}
                              </span>
                              {employee.department_name && (
                                <span className="text-[10px] text-muted-foreground truncate max-w-[180px]" title={employee.department_name}>
                                  {employee.department_name}
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Day cells */}
                          {dates.map((date, idx) => {
                            const dateStr = dateStrings[idx]
                            const day = employee.days[dateStr]

                            return (
                              <TimesheetDayCell
                                key={dateStr}
                                day={day}
                                dateStr={dateStr}
                                isToday={isToday(date)}
                                isWeekend={isWeekend(date)}
                                onClick={() => {
                                  if (day) {
                                    onDayClick({ employee, dateStr, day })
                                  }
                                }}
                              />
                            )
                          })}
                          {/* Summary cells */}
                          <td className="sticky right-[80px] z-10 p-1 text-center text-xs font-medium border-l bg-green-50">
                            {employee.totals.worked_hours}
                          </td>
                          <td className="sticky right-[40px] z-10 p-1 text-center text-xs font-medium border-l bg-blue-50">
                            {employee.totals.norm_hours}
                          </td>
                          <td
                            className={cn(
                              'sticky right-0 z-10 p-1 text-center text-xs font-medium border-l',
                              employee.totals.delta_hours > 0
                                ? 'bg-green-100 text-green-700'
                                : employee.totals.delta_hours < 0
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-gray-100',
                            )}
                          >
                            {formatDelta(employee.totals.delta_hours)}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Legend */}
      <div className="border-t p-3 flex flex-wrap gap-4 text-xs bg-muted/30">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-200" />
          <span className="text-muted-foreground">Отработано</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-orange-100 border border-orange-200" />
          <span className="text-muted-foreground">Опоздание</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-50 border border-red-200" />
          <span className="text-muted-foreground">Неявка</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
          <span className="text-muted-foreground">Выходной</span>
        </div>
        <div className="text-muted-foreground ml-4">
          <span className="font-medium">В</span> - выходной, <span className="font-medium">Н</span> - неявка
        </div>
      </div>
    </div>
  )
}
